<template>
  <GridChartCard
  :title="title"
  ref="chartCard"
  :hasData="hasData"
  @download-csv="exportCSV"
  @draw="createChart"
  :noDataText="noDataText"
  >
    <template #actions>
      <Icon
        iconColor="white"
        :tooltipText="showProducts ? $t('total') : $t('byProductVerbatim')"
        :small="false"
        :disabled="!hasData"
        @icon-clicked="showProducts = !showProducts"
        :icon="showProducts ? 'mdi-pine-tree-variant' : 'mdi-pine-tree-variant-outline'"
        elementId="toggle-products-average"
      />
    </template>
  </GridChartCard>
</template>

<script>
import { Chart } from 'chart.js'
import { formatMoney, numberWithCommas } from '@/utils/NumericMutations.js'
import { saveAs } from 'file-saver'
import moment from 'moment'
import { LocalStorageKeys } from '@/utils/LocalStorageActor'
import { getChartColor } from './ChartColors'
import { mapActions, mapGetters } from 'vuex'
import { theme } from '@/plugins/theme'
import { chartTooltipStyle } from '@/utils/ChartHelpers'

const INVALID_LOCATION_ID = -1

export default {
  name: 'LogAnalysisChart',

  components: {
    GridChartCard: () => import('./GridChartCard.vue'),
    Icon: () => import('@/components/helper/Icon.vue')
  },

  props: {
    logAnalysis: {
      type: Array,
      required: true
    },
    elementId: {
      type: String,
      required: true
    },
    selectedLocation: {
      type: Object
    },
    isInteractive: {
      type: Boolean,
      default: false
    }
  },

  data: () => ({
    max: 0,
    dataObj: undefined,
    csvDataObj: undefined,
    chart: null,
    showProducts: localStorage.getItem(LocalStorageKeys.DEFAULT_LOG_DAILY_INVENTORY_COST_SHOW_AVERAGE) !== 'false',
    hiddenDataSetIndices: []
  }),

  computed: {
    ...mapGetters('dashboard', ['interactiveChartTooltipFooter']),
    ...mapGetters('dashboard', ['ticketSummarySelectedDestinationId']),
    ...mapGetters('ticket', ['contractMode']),

    noDataText () {
      return this.ticketSummarySelectedDestinationId === INVALID_LOCATION_ID ? this.$t('dataUnavailableMoreThanOneDestination') : undefined
    },

    title () {
      return `${this.$t('dailyInventoryCost')} (Avg: ${this.averageCost}/t)`
    },

    averageCost () {
      const { tons, cost } = this.logAnalysis.reduce((acc, la) => {
        acc.tons += la.totalTons
        acc.cost += la.totalCost
        return acc
      }, { cost: 0, tons: 0 })

      if (tons === 0 || cost === 0) {
        return formatMoney(0)
      }

      return formatMoney(parseFloat((cost / tons).toFixed(2)))
    },

    hasData () {
      return this.selectedLocation.locationId !== undefined && this.selectedLocation.locationId !== -1 && this.logAnalysis.length !== 0
    }
  },

  watch: {
    logAnalysis () {
      this.hiddenDataSetIndices = []
      this.triggerDraw()
    },
    showProducts (val) {
      this.hiddenDataSetIndices = []
      localStorage.setItem(LocalStorageKeys.DEFAULT_LOG_DAILY_INVENTORY_COST_SHOW_AVERAGE, val.toString())
      this.triggerDraw()
    }
  },

  methods: {
    ...mapActions('dashboard', ['getWidgetInteractionStatus']),

    triggerDraw () {
      this.$nextTick(() => this.$refs.chartCard.draw())
    },

    getDatasets () {
      let labels
      if (this.logAnalysis.length > 0) {
        labels = this.logAnalysis.map(data => moment(data.day).format('MM/DD/YYYY'))
      } else {
        return
      }

      if (this.showProducts) {
        const allProducts = new Set()
        this.logAnalysis.forEach(dayData => {
          if (dayData.products) {
            dayData.products.forEach(product => allProducts.add(product.name))
          }
        })

        const productDatasets = Array.from(allProducts).map((productName, index) => {
          let color = ''
          const productData = labels.map((_, dayIndex) => {
            const dayData = this.logAnalysis[dayIndex]
            const product = dayData.products?.find(p => p.name === productName)
            return product ? product.averageCost : 0
          })

          color = getChartColor(index, allProducts.size, this.contractMode.value)

          return {
            data: productData,
            label: productName,
            fill: false,
            borderColor: color,
            pointBackgroundColor: color,
            cubicInterpolationMode: 'monotone',
            hidden: this.hiddenDataSetIndices.includes(index)
          }
        })

        this.dataObj = {
          date: labels,
          products: this.logAnalysis.map(dayData => dayData.products || [])
        }
        this.csvDataObj = {
          date: labels,
          averageCost: { data: this.logAnalysis.map(data => data.averageCost), label: this.$t('averageCost:') },
          totalCost: { data: this.logAnalysis.map(data => data.totalCost), label: this.$t('totalCost:') },
          totalProductionCost: { data: this.logAnalysis.map(data => data.totalProductionCost), label: this.$t('totalProductionCost:') },
          totalStumpageCost: { data: this.logAnalysis.map(data => data.totalStumpageCost), label: this.$t('totalStumpageCost:') },
          totalTons: { data: this.logAnalysis.map(data => data.totalTons), label: this.$t('totalTons:') },
          loadCount: { data: this.logAnalysis.map(data => data.loadCount), label: this.$t('loadCount:') }
        }

        return {
          datasets: productDatasets,
          labels: labels
        }
      } else {
        this.dataObj = {
          date: labels,
          averageCost: { data: this.logAnalysis.map(data => data.averageCost), label: this.$t('averageCost:') },
          totalCost: { data: this.logAnalysis.map(data => data.totalCost), label: this.$t('totalCost:') },
          totalProductionCost: { data: this.logAnalysis.map(data => data.totalProductionCost), label: this.$t('totalProductionCost:') },
          totalStumpageCost: { data: this.logAnalysis.map(data => data.totalStumpageCost), label: this.$t('totalStumpageCost:') },
          totalTons: { data: this.logAnalysis.map(data => data.totalTons), label: this.$t('totalTons:') },
          loadCount: { data: this.logAnalysis.map(data => data.loadCount), label: this.$t('loadCount:') }
        }
        this.csvDataObj = this.dataObj

        const averageCostDataSet = [{
          data: this.dataObj.averageCost.data,
          label: 'Average Cost Per Day',
          fill: false,
          borderColor: theme.primary,
          pointBackgroundColor: theme.primary,
          cubicInterpolationMode: 'monotone'
        }]

        return {
          datasets: averageCostDataSet,
          labels: labels
        }
      }
    },

    async onDataClicked (e) {
      if (this.isInteractive) {
        const element = this.chart.getElementsAtEventForMode(e, 'point', true)
        const data = this.chart.config._config.data
        const xAxisValue = data.labels[element[0]?.index]
        if (this.showProducts) {
          const yAxisValue = data.datasets[element[0]?.datasetIndex]?.label
          if (await this.getWidgetInteractionStatus({ widgetId: this.widgetId, x: xAxisValue, y: yAxisValue })) this.$emit('data-clicked', { xAxisValue: xAxisValue, yAxisValue: yAxisValue })
        } else {
          if (await this.getWidgetInteractionStatus({ widgetId: this.widgetId, x: xAxisValue })) this.$emit('data-clicked', { xAxisValue: xAxisValue, yAxisValue: undefined })
        }
      }
    },

    createChart (canvas) {
      this.dataObj = undefined
      const chartData = this.getDatasets()
      const localData = this.dataObj

      const legendClickHandler = (e, legendItem, legend) => {
        const index = legendItem.datasetIndex
        const chart = legend.chart
        if (chart.isDatasetVisible(index)) {
          chart.hide(index)
          legendItem.hidden = true
          this.hiddenDataSetIndices.push(index)
        } else {
          chart.show(index)
          legendItem.hidden = false
          this.hiddenDataSetIndices = this.hiddenDataSetIndices.filter(dsIndex => dsIndex !== index)
        }
      }

      const chart = new Chart(canvas, {
        type: 'line',
        data: chartData,
        options: {
          responsive: true,
          maintainAspectRatio: false,
          resizeDelay: 500,
          interaction: {
            mode: 'point',
            intersect: false
          },
          plugins: {
            tooltip: {
              callbacks: {
                label: function (tooltipItem) {
                  if (localData.products) {
                    const dayProducts = localData.products[tooltipItem.dataIndex] || []
                    const product = dayProducts.find(p => p.name === tooltipItem.dataset.label)

                    if (product) {
                      return [
                        `${product.name}: ${numberWithCommas(product.totalTons, 3)} tons`,
                        `Average Cost: ${formatMoney(product.averageCost)}`,
                        `Total Cost: ${formatMoney(product.totalCost)}`,
                        `Production Cost: ${formatMoney(product.totalProductionCost)}`,
                        `Stumpage Cost: ${formatMoney(product.totalStumpageCost)}`
                      ]
                    } else {
                      return `${tooltipItem.dataset.label}: 0 tons`
                    }
                  } else {
                    const averageCost = tooltipItem.parsed.y
                    const tooltipText = [`${localData.averageCost.label} ${formatMoney(averageCost)}`]
                    tooltipText.push(`${localData.totalCost.label} ${formatMoney(localData.totalCost.data[tooltipItem.dataIndex])}`)
                    tooltipText.push(`${localData.totalProductionCost.label} ${formatMoney(localData.totalProductionCost.data[tooltipItem.dataIndex])}`)
                    tooltipText.push(`${localData.totalStumpageCost.label} ${formatMoney(localData.totalStumpageCost.data[tooltipItem.dataIndex])}`)
                    tooltipText.push(`${localData.totalTons.label} ${numberWithCommas(localData.totalTons.data[tooltipItem.dataIndex], 3)}`)
                    tooltipText.push(`${localData.loadCount.label} ${numberWithCommas(localData.loadCount.data[tooltipItem.dataIndex])}`)
                    return tooltipText
                  }
                }
              },
              ...chartTooltipStyle
            },
            legend: {
              onClick: legendClickHandler
            }
          },
          scales: {
            x: {
              grid: {
                display: true
              }
            },
            y: {
              display: true,
              grid: {
                display: true
              },
              ticks: {
                callback: function (val, index) {
                  return formatMoney(val)
                }
              }
            }
          },
          onClick: (e) => this.onDataClicked(e)
        }
      })
      if (this.isInteractive) chart.options.plugins.tooltip.callbacks.footer = () => this.interactiveChartTooltipFooter
      this.chart = chart
    },

    exportCSV () {
      const headers = 'Date, Average Cost, Total Cost, Total Production Cost, Total Stumpage Cost, Total Tons, Load Count \n'
      let dataString = ''
      this.csvDataObj.averageCost.data.forEach((_, index) => {
        dataString += `${this.csvDataObj.date[index]}, $${this.csvDataObj.averageCost.data[index]}, $${this.csvDataObj.totalCost.data[index]}, $${this.csvDataObj.totalProductionCost.data[index]}, $${this.csvDataObj.totalStumpageCost.data[index]}, ${this.csvDataObj.totalTons.data[index]}, ${this.csvDataObj.loadCount.data[index]}\n`
      })
      const blob = new Blob([headers + dataString], { type: 'text/plain;charset=utf-8' })
      saveAs(blob, `${this.selectedLocation.name.replace(/\s/g, '')}InventoryCostTrend.csv`)
    }
  }
}
</script>
